export interface ChunkLoadErrorDetails {
    chunkId: string;
    chunkType: 'css' | 'js';
    failureType: 'error' | 'missing' | 'timeout' | 'unknown';
    url: string;
}

/** Диагностика ошибки загрузки чанка. */
export interface ChunkLoadErrorAdditional {
    /** ID чанка. */
    bundle_chunk_id: string;
    /** Тип ресурса. */
    bundle_chunk_resource_type: ChunkLoadErrorDetails['chunkType'];
    /** Тип сбоя загрузчика; unknown при отсутствии данных. */
    bundle_chunk_failure_type: ChunkLoadErrorDetails['failureType'];
    /** URL ресурса. */
    bundle_chunk_url: string;
}

const CHUNK_LOAD_ERROR_MESSAGES = {
    css: 'CSS bundle unavailable',
    js: 'JS bundle unavailable',
};

export function getChunkLoadErrorMessage(chunkType: ChunkLoadErrorDetails['chunkType']): string {
    return CHUNK_LOAD_ERROR_MESSAGES[chunkType];
}

export function getChunkLoadErrorAdditional(details: ChunkLoadErrorDetails): ChunkLoadErrorAdditional {
    return {
        bundle_chunk_id: details.chunkId,
        bundle_chunk_resource_type: details.chunkType,
        bundle_chunk_failure_type: details.failureType,
        bundle_chunk_url: details.url,
    };
}

// Rspack: настоящий перенос строки в JS-ошибке, буквальный backslash+n в CSS-ошибке.
const CHUNK_LOAD_ERROR_REGEXP =
    /^(?:Unhandled rejection: )?Loading (CSS )?chunk (\S+) failed\.(?:\s|\\[rn])*\((?:(error|missing|timeout):\s*)?(.+)\)$/;

function getErrorMessage(error: unknown): string | null {
    if (typeof error === 'string') {
        return error;
    }

    if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
        return error.message;
    }

    return null;
}

/**
 * Разбирает сообщения загрузчика чанков Rspack.
 * @param error Ошибка или сообщение, в том числе после нормализации Error Booster.
 * @returns Тип бандла и диагностика загрузчика, иначе null.
 * @kind helper
 */
export function parseChunkLoadError(error: unknown): ChunkLoadErrorDetails | null {
    const message = getErrorMessage(error);

    if (message === null) {
        return null;
    }

    const match = CHUNK_LOAD_ERROR_REGEXP.exec(message);

    if (match === null) {
        return null;
    }

    const [, cssMarker, chunkId, failureType, url] = match;

    return {
        chunkId,
        chunkType: cssMarker === undefined ? 'js' : 'css',
        failureType: (failureType ?? 'unknown') as ChunkLoadErrorDetails['failureType'],
        url,
    };
}
